if (!customElements.get('site-header')) {
  customElements.define(
    'site-header',
    class Header extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.headerGroupElement = this.closest('.shopify-section-group-header-group');
        this.headerHeight = this.headerGroupElement?.scrollHeight;
        this.header = this.closest('.header');
        this.isOverlay = this.dataset.overlay === 'true';
        this.fadeInDone = false;

        this.updateHeaderOffset();
        document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
        document.documentElement.style.setProperty('--pdp-gallery-top', `${this.headerHeight + 16}px`);

        if (this.isOverlay) {
          document.body.classList.add('header-overlay');
        }

        this.header.addEventListener('animationend', (animationEndEvent) => {
          if (animationEndEvent.animationName === 'fadeIn') {
            this.header.classList.remove('animate-fadeIn');
            this.fadeInDone = true;
          }
        });

        if (this.headerGroupElement) {
          new ResizeObserver(() => {
            this.headerHeight = this.headerGroupElement.scrollHeight;
            this.updateHeaderOffset();
            document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
          }).observe(this.headerGroupElement);
        }

        window.addEventListener('resize', () => {
          this.headerHeight = this.headerGroupElement?.scrollHeight;
          this.updateHeaderOffset();
          document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
        });

        window.addEventListener('scroll', () => {
          if (this.isOverlay) {
            this.header.classList.toggle('header--scrolled', window.scrollY > 100);
          }

          if ((window.scrollY > this.lastScroll) && (window.scrollY > (this.headerHeight * 10))) {
            this.header.classList.remove('animate-fadeIn');
            this.header.classList.add('animate-fadeOut');
            this.fadeInDone = false;
            if (this.activeNavBlockId) {
              window.dispatchEvent(new CustomEvent('modal:closeById', { detail: this.navPanelModalId }));
            }
            this.startOffsetTracking();
          } else if ((window.scrollY < this.lastScroll) || ((window.scrollY < (this.headerHeight * 10) && this.classList.contains('animate-fadeOut')))) {
            this.header.classList.remove('animate-fadeOut');
            if (!this.fadeInDone) {
              this.header.classList.add('animate-fadeIn');
            }
            this.startOffsetTracking();
          }

          this.updateHeaderOffset();

          this.lastScroll = window.scrollY;
        }, { passive: true });

        const shopifySectionElement = this.closest('.shopify-section') ?? this.parentElement;
        const navPanelTemplate = shopifySectionElement?.querySelector('template[data-nav-modal]');
        this.navPanelModalId = navPanelTemplate?.dataset.modalContent;
        this.activeNavBlockId = null;
        this.activeSubMenuHandle = null;

        this.setupMobileNav(shopifySectionElement);

        if (!this.navPanelModalId) return;

        this.querySelectorAll('[data-nav-trigger]').forEach(navTriggerButton => {
          navTriggerButton.addEventListener('click', () => {
            const clickedBlockId = navTriggerButton.dataset.navTrigger;
            const customModalElement = document.querySelector('custom-modal');
            const navPanelIsOpen = !!customModalElement?.querySelector(`[data-modal-content="${this.navPanelModalId}"]:not(template)`);

            if (navPanelIsOpen && this.activeNavBlockId === clickedBlockId) {
              window.dispatchEvent(new CustomEvent('modal:closeById', { detail: this.navPanelModalId }));
            } else {
              if (!navPanelIsOpen) {
                this.updateHeaderOffset();
                window.dispatchEvent(new CustomEvent('modal:openById', { detail: this.navPanelModalId }));
              }
              this.showNavBlockContent(clickedBlockId);
            }
          });
        });

        document.addEventListener('click', (clickEvent) => {
          const subMenuTriggerButton = clickEvent.target.closest('[data-nav-sub-trigger]');
          if (!subMenuTriggerButton) return;
          const renderedNavModal = this.getRenderedNavModal();
          if (!renderedNavModal?.contains(subMenuTriggerButton)) return;
          this.toggleSubMenuPanel(subMenuTriggerButton.dataset.navSubTrigger);
        });

        this.querySelectorAll('[data-modal-action="open"]').forEach(modalOpenButton => {
          modalOpenButton.addEventListener('click', () => {
            if (this.activeNavBlockId) {
              window.dispatchEvent(new CustomEvent('modal:closeById', { detail: this.navPanelModalId }));
            }
            if (this.mobileNavModalId) {
              const mobileNavOpen = !!document.querySelector(`custom-modal [data-modal-content="${this.mobileNavModalId}"]:not(template)`);
              if (mobileNavOpen) {
                window.dispatchEvent(new CustomEvent('modal:closeById', { detail: this.mobileNavModalId }));
              }
            }
          });
        });

        const customModalElement = document.querySelector('custom-modal');
        if (customModalElement) {
          new MutationObserver(() => {
            const navPanelStillOpen = !!customModalElement.querySelector(`[data-modal-content="${this.navPanelModalId}"]:not(template)`);
            if (!navPanelStillOpen && this.activeNavBlockId) {
              this.activeNavBlockId = null;
              this.activeSubMenuHandle = null;
              this.querySelectorAll('[data-nav-trigger]').forEach(navTriggerButton => {
                navTriggerButton.removeAttribute('data-nav-active');
                navTriggerButton.setAttribute('aria-expanded', 'false');
              });
            }

            // Nav panel is not a dialog — remove aria-modal so browser doesn't enforce its own trap
            const navPanel = customModalElement.querySelector('[data-nav-modal]:not(template)');
            if (navPanel) navPanel.removeAttribute('aria-modal');
          }).observe(customModalElement, { childList: true });
        }

        document.addEventListener('keydown', (keydownEvent) => {
          if (keydownEvent.key !== 'Escape') return;
          if (!this.activeSubMenuHandle) return;
          keydownEvent.stopImmediatePropagation();
          const renderedNavModal = this.getRenderedNavModal();
          if (!renderedNavModal) return;
          const activeSubTriggerButton = renderedNavModal.querySelector(`[data-nav-sub-trigger="${this.activeSubMenuHandle}"]`);
          this.collapseSubMenuPanel(renderedNavModal);
          activeSubTriggerButton?.focus();
        });
      }

      setupMobileNav(shopifySectionElement) {
        const mobileNavTemplate = shopifySectionElement?.querySelector('template[data-mobile-nav]');
        if (!mobileNavTemplate) return;

        this.mobileNavModalId = mobileNavTemplate.dataset.modalContent;
        this.mobileNavStack = ['root'];

        const burgerBtn = this.querySelector('[data-mobile-nav-trigger]');
        if (burgerBtn) {
          burgerBtn.addEventListener('click', () => {
            const isOpen = !!document.querySelector(`custom-modal [data-modal-content="${this.mobileNavModalId}"]:not(template)`);
            if (isOpen) {
              window.dispatchEvent(new CustomEvent('modal:closeById', { detail: this.mobileNavModalId }));
            } else {
              window.dispatchEvent(new CustomEvent('modal:openById', { detail: this.mobileNavModalId }));
              burgerBtn.setAttribute('aria-expanded', 'true');
            }
          });
        }

        document.addEventListener('click', e => {
          const pushBtn = e.target.closest('[data-mobile-push]');
          if (pushBtn) {
            const nav = document.querySelector('custom-modal [data-mobile-nav]');
            if (!nav) return;
            this.mobilePush(nav, pushBtn.dataset.mobilePush);
            return;
          }
          const backBtn = e.target.closest('[data-mobile-back]');
          if (backBtn) {
            const nav = document.querySelector('custom-modal [data-mobile-nav]');
            if (!nav) return;
            this.mobilePop(nav);
          }
        });

        const customModalElement = document.querySelector('custom-modal');
        if (customModalElement) {
          new MutationObserver(() => {
            const isOpen = !!customModalElement.querySelector(`[data-modal-content="${this.mobileNavModalId}"]:not(template)`);
            if (!isOpen) {
              this.mobileNavStack = ['root'];
              burgerBtn?.setAttribute('aria-expanded', 'false');
            }
          }).observe(customModalElement, { childList: true });
        }
      }

      mobilePush(nav, panelId) {
        const currentId = this.mobileNavStack[this.mobileNavStack.length - 1];
        const currentPanel = nav.querySelector(`[data-mobile-panel="${currentId}"]`);
        const nextPanel = nav.querySelector(`[data-mobile-panel="${panelId}"]`);
        if (!nextPanel) return;

        currentPanel?.setAttribute('data-mobile-panel-state', 'left');
        nextPanel.setAttribute('data-mobile-panel-state', 'active');
        this.mobileNavStack.push(panelId);

        nav.querySelector(`[data-mobile-push="${panelId}"]`)?.setAttribute('aria-expanded', 'true');
      }

      mobilePop(nav) {
        if (this.mobileNavStack.length <= 1) return;
        const currentId = this.mobileNavStack.pop();
        const previousId = this.mobileNavStack[this.mobileNavStack.length - 1];

        const currentPanel = nav.querySelector(`[data-mobile-panel="${currentId}"]`);
        const previousPanel = nav.querySelector(`[data-mobile-panel="${previousId}"]`);

        currentPanel?.removeAttribute('data-mobile-panel-state');
        previousPanel?.setAttribute('data-mobile-panel-state', 'active');

        nav.querySelector(`[data-mobile-push="${currentId}"]`)?.setAttribute('aria-expanded', 'false');
      }

      startOffsetTracking() {
        if (this._trackingRaf) return;
        const track = () => {
          this.updateHeaderOffset();
          if (this.header.classList.contains('animate-fadeIn') || this.header.classList.contains('animate-fadeOut')) {
            this._trackingRaf = requestAnimationFrame(track);
          } else {
            this._trackingRaf = null;
            this.updateHeaderOffset();
          }
        };
        this._trackingRaf = requestAnimationFrame(track);
      }

      updateHeaderOffset() {
        document.documentElement.style.setProperty('--header-offset', `${Math.max(this.header.getBoundingClientRect().bottom, 0)}px`);
      }

      getRenderedNavModal() {
        return document.querySelector('custom-modal [data-nav-modal]');
      }

      showNavBlockContent(blockId) {
        const renderedNavModal = this.getRenderedNavModal();
        if (!renderedNavModal) return;

        if (this.activeNavBlockId && this.activeNavBlockId !== blockId) {
          renderedNavModal.querySelector(`[data-nav-panel-content="${this.activeNavBlockId}"]`)?.classList.add('hidden');
          renderedNavModal.querySelector(`[data-nav-banner-content="${this.activeNavBlockId}"]`)?.classList.add('hidden');
          if (this.activeSubMenuHandle) {
            this.collapseSubMenuPanel(renderedNavModal);
          }
        }

        const navBlockContentElement = renderedNavModal.querySelector(`[data-nav-panel-content="${blockId}"]`);
        if (!navBlockContentElement) return;
        navBlockContentElement.classList.remove('hidden');
        renderedNavModal.querySelector(`[data-nav-banner-content="${blockId}"]`)?.classList.remove('hidden');
        this.activeNavBlockId = blockId;
        this.updateRightColVisibility(renderedNavModal);

        const activeTrigger = this.querySelector(`[data-nav-trigger="${blockId}"]`);
        this.querySelectorAll('[data-nav-trigger]').forEach(navTriggerButton => {
          const isActive = navTriggerButton.dataset.navTrigger === blockId;
          navTriggerButton.toggleAttribute('data-nav-active', isActive);
          navTriggerButton.setAttribute('aria-expanded', isActive ? 'true' : 'false');
        });

        this._setupNavFocusTrap(renderedNavModal, activeTrigger);
      }

      _setupNavFocusTrap(navPanel, trigger) {
        if (navPanel._navTrapHandler) {
          navPanel.removeEventListener('keydown', navPanel._navTrapHandler, true);
        }

        const focusableSelector = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

        const handler = e => {
          if (e.key !== 'Tab') return;
          const focusable = Array.from(navPanel.querySelectorAll(focusableSelector))
            .filter(el => el.offsetParent !== null);
          if (!focusable.length) return;
          const first = focusable[0];
          const last  = focusable[focusable.length - 1];

          if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            e.stopImmediatePropagation();
            trigger?.focus();
          } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            e.stopImmediatePropagation();
            first.focus();
          }
        };

        navPanel._navTrapHandler = handler;
        navPanel.addEventListener('keydown', handler, true)

        setTimeout(() => {
          const focusable = Array.from(navPanel.querySelectorAll(focusableSelector))
            .filter(el => el.offsetParent !== null);
          focusable[0]?.focus();
        }, 60);
      }

      toggleSubMenuPanel(subMenuHandle) {
        const renderedNavModal = this.getRenderedNavModal();
        if (!renderedNavModal) return;

        renderedNavModal.querySelectorAll('[data-nav-sub-content].is-exiting').forEach(exitingEl => {
          exitingEl.classList.remove('is-exiting');
          exitingEl.classList.add('hidden');
        });

        if (this.activeSubMenuHandle === subMenuHandle) {
          this.collapseSubMenuPanel(renderedNavModal);
        } else {
          if (this.activeSubMenuHandle) {
            const previousSubMenuContent = renderedNavModal.querySelector(`[data-nav-sub-content="${this.activeSubMenuHandle}"]`);
            if (previousSubMenuContent) {
              previousSubMenuContent.classList.add('hidden');
              void previousSubMenuContent.offsetHeight;
            }
            const previousSubTrigger = renderedNavModal.querySelector('[data-nav-sub-active]');
            previousSubTrigger?.removeAttribute('data-nav-sub-active');
            previousSubTrigger?.setAttribute('aria-expanded', 'false');
          } else {
            renderedNavModal.querySelector(`[data-nav-banner-content="${this.activeNavBlockId}"]`)?.classList.add('hidden');
          }
          const newSubMenuContent = renderedNavModal.querySelector(`[data-nav-sub-content="${subMenuHandle}"]`);
          if (!newSubMenuContent) return;
          newSubMenuContent.classList.remove('hidden');
          this.activeSubMenuHandle = subMenuHandle;
          newSubMenuContent.querySelector('a')?.focus();
          const newSubTrigger = renderedNavModal.querySelector(`[data-nav-sub-trigger="${subMenuHandle}"]`);
          newSubTrigger?.setAttribute('data-nav-sub-active', '');
          newSubTrigger?.setAttribute('aria-expanded', 'true');
          this.updateRightColVisibility(renderedNavModal);
        }
      }

      collapseSubMenuPanel(renderedNavModal) {
        const subContentElement = this.activeSubMenuHandle
          ? renderedNavModal.querySelector(`[data-nav-sub-content="${this.activeSubMenuHandle}"]`)
          : null;
        const bannerContentElement = renderedNavModal.querySelector(`[data-nav-banner-content="${this.activeNavBlockId}"]`);

        const collapsingSubTrigger = renderedNavModal.querySelector('[data-nav-sub-active]');
        collapsingSubTrigger?.removeAttribute('data-nav-sub-active');
        collapsingSubTrigger?.setAttribute('aria-expanded', 'false');
        this.activeSubMenuHandle = null;
        this.updateRightColVisibility(renderedNavModal);

        if (subContentElement) {
          subContentElement.classList.add('is-exiting');
          subContentElement.addEventListener('animationend', () => {
            subContentElement.classList.remove('is-exiting');
            subContentElement.classList.add('hidden');
            if (bannerContentElement && bannerContentElement.dataset.navBannerContent === this.activeNavBlockId) {
              bannerContentElement.classList.remove('hidden');
            }
          }, { once: true });
        } else {
          bannerContentElement?.classList.remove('hidden');
        }
      }

      updateRightColVisibility(renderedNavModal) {
        const bannerDiv = renderedNavModal.querySelector(`[data-nav-banner-content="${this.activeNavBlockId}"]`);
        const hasBanners = bannerDiv && bannerDiv.children.length > 0;
        const visible = hasBanners || !!this.activeSubMenuHandle;
        renderedNavModal.querySelector('[data-nav-divider]')?.toggleAttribute('data-nav-collapsed', !visible);
        renderedNavModal.querySelector('[data-nav-right-col]')?.toggleAttribute('data-nav-collapsed', !visible);
      }

    }
  )
}
